import { type AlertType } from '@alltrails/shared/types/alert';
import type Waypoint from '@alltrails/waypoints/types/Waypoint';
import User from '@alltrails/core/types/User';
import type GarminCourse from '@alltrails/core/types/GarminCourse';
import { PrivacyPolicyOptions } from '@alltrails/shared/types/privacyPolicy';
import type { MetadataStartLocation } from '../redux/customRoutesApi';
import { AllTrailsBounds, ElevationPoint, LatLng } from '../types/Geo';

export type EstimatedTimeToComplete = {
  value: string;
  unit: string;
};

export type Location = {
  postalCode?: string;
  city?: string;
  region?: string;
  country?: string;
  countryId?: number;
  latitude?: string;
  longitude?: string;
};

// Note that this type only defines the fields needed for custom routes; it does not define all fields returned by the API
export type CustomRouteMap = {
  id: number;
  bounds: {
    longitudeTopLeft: string | number;
    latitudeTopLeft: string | number;
    longitudeBottomRight: string | number;
    latitudeBottomRight: string | number;
  };
  name?: string;
  description?: string;
  contentPrivacy?: PrivacyPolicyOptions;
  slug: string;
  activity: { uid: string };
  summaryStats: { distanceTotal: number; elevationGain: number; duration: number };
  estimatedTimeToComplete?: EstimatedTimeToComplete;
  routes: {
    id: number;
    sequence_num: number;
    lineGeoStats: { timeTotal?: number; distanceTotal: number; elevationGain: number };
    lineDisplayProperty?: { color: string };
    lineSegments: { sequence_num: number; polyline: { pointsData: string }; freeDraw?: boolean }[];
    estimatedTimeToComplete?: EstimatedTimeToComplete;
  }[];
  waypoints: Waypoint[];
  metadata: { created: string; updated: string };
  garminCourse?: GarminCourse;
  location: Location;
  user: User;
};

export type CustomRoutePath = {
  points: LatLng[];
  freeDraw?: boolean;
};

// Note that this ID is for the sake of ordering and does not correspond to the ID returned from the API
// We define this type since custom routes do not use all the fields returned by the API
export type CustomRouteSegmentId = number;
export type CustomRouteSegment = {
  id: CustomRouteSegmentId;
  color: string;
  elevationPoints?: ElevationPoint[];
  paths: CustomRoutePath[];
  distance?: number;
  elevationGain?: number;
  estimatedTime?: EstimatedTimeToComplete;
  serverId?: number;
  startLocation?: MetadataStartLocation;
};

// Note that this ID is for the sake of ordering and does not correspond to the ID returned from the API
// We define this type since custom routes do not use all the fields returned by the API
export type CustomRouteWaypointId = number;
export type CustomRouteWaypoint = {
  id: CustomRouteWaypointId;
  name: string;
  description?: string;
  showTitle?: boolean;
  latitude: number;
  longitude: number;
  serverId?: number;
  isTemporary?: boolean;
};

export enum ActivityType {
  Hiking = 'hiking',
  MountainBiking = 'mountain-biking',
  RoadBiking = 'road-biking',
  OffRoadDriving = 'off-road-driving',
  ScenicDriving = 'scenic-driving'
}

export type CustomRouteDetails = {
  id?: number;
  bounds?: AllTrailsBounds;
  name?: string;
  description?: string;
  contentPrivacy?: PrivacyPolicyOptions;
  slug?: string;
  activityType: ActivityType;
  distance?: number;
  elevationGain?: number;
  estimatedTime?: {
    value: string;
    unit: string;
  };
  segments: CustomRouteSegment[];
  waypoints: CustomRouteWaypoint[];
  createdAt?: string;
  garminCourse?: GarminCourse;
  location?: Location;
};

export enum CustomRoutesSort {
  DateUpdated = 'date_updated',
  DateCreated = 'date_created',
  Name = 'name'
}

export type CustomRouteType = 'map';

export type UserCustomRoute = {
  id: number;
  _geoloc: {
    lat: number;
    lng: number;
  };
  activities: string[];
  activity: {
    uid: string;
    name: string;
  };
  activity_names: string[];
  avg_rating: number | null;
  city_name: string;
  contentPrivacy: PrivacyPolicyOptions;
  country_name: string;
  created_at: string;
  updated_at: string;
  distance: number;
  duration_minutes: number;
  elevation_gain: number;
  estimated_time_to_complete: {
    value: string;
    unit: string;
  };
  hidden: number;
  indexed_at: number;
  length: number;
  missing_polyline: boolean;
  name: string;
  objectID: string;
  photo_count: number;
  popularity: number;
  private: boolean;
  rating: number | null;
  slug: string;
  state_name: string;
  timezone: string | null;
  trail_id: number;
  type: CustomRouteType;
  units: string;
  user: {
    id: number;
    first_name: string;
    last_name: string;
    slug: string;
    profile_photo_url: string;
  };
};

export type MarkerProperties = {
  id: string;
  segmentId: number;
  isStart?: boolean;
  isEnd?: boolean;
  pathIndex: number;
  endPathIndex?: number;
  latitude: number;
  longitude: number;
};

export type LineProperties = {
  id: string;
  pathIndex: number;
  lineColor: string;
  lineOutlineColor: string;
  freeDraw?: boolean;
};

export type SegmentPolyline = Pick<CustomRouteSegment, 'id' | 'color' | 'paths'>;

export type PathGroup = {
  paths: CustomRoutePath[];
};
export type GroupedSegment = {
  pathGroups: PathGroup[];
};

export type Costings = {
  leastGain: number;
  mostScenic: number;
  mostPopular: number;
  makeShorter: number;
};

export type CostingsKeys = keyof Costings;

export type CostingsConfig = {
  [costing in CostingsKeys]: {
    minimum: number;
    maximum: number;
    default: number;
  };
};

export type CustomRouteAlert = {
  iconCode: string;
  level: string;
  message: string;
  type: AlertType;
};
